import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  Fragment,
} from "react";
import {
  AdjustmentsHorizontalIcon,
  PlusIcon,
  TrashIcon,
  BookmarkIcon,
  PlayIcon,
  PauseIcon,
  PencilSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import { Button } from "../ui/Button";
import { Input } from "../ui/Form/Input";
import { CustomDropdown } from "./filters/CustomDropdown";
import { MultiSelectFilterDropdown } from "./filters/MultiSelectFilterDropdown";
import { DatePicker } from "components/shared/form/Datepicker";
import {
  getAdvancedFilterFieldsWithOperators,
  getMasterFields,
  saveAdvancedFilterFields,
  getSavedFilters,
  deleteSavedFilter,
} from "../../services/leadsService";
import { getUsedJobMasterFields } from "../../services/jobsService";
import { toast } from "sonner";
import { ConfirmModal } from "../shared/ConfirmModal";
import { createPortal } from "react-dom";
import { Transition, TransitionChild } from "@headlessui/react";
import clsx from "clsx";

let idCounter = 0;
const generateId = () => {
  idCounter += 1;
  return `${Date.now()}-${idCounter}`;
};

export function AdvancedFilters({
  onApplyFilters,
  currentFilters = {},
  appliedFilters = {},
  advancedFilterFields = [],
  supportedOperators = [],
  activeTab = "filter",
  onTabChange,
  onSavedCountChange,
  tagsData = [], // Array of { Id, TagName } for ID-based tag options
  entityType = "lead",
}) {
  const [fieldGroups, setFieldGroups] = useState([]);

  const [groupLogic, setGroupLogic] = useState("AND");
  const [isApplying, setIsApplying] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownRect, setDropdownRect] = useState(null);
  const [searchTerms, setSearchTerms] = useState({});
  const [masterFields, setMasterFields] = useState([]);
  const [operators, setOperators] = useState({});
  const [dropdowns, setDropdowns] = useState({});
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const isResetting = useRef(false);
  const valueRefs = useRef({});
  const searchInputRefs = useRef({});
  const dropdownButtonRefs = useRef({});

  // Function to update dropdown position based on its trigger button
  const updateDropdownPosition = useCallback((key) => {
    const button = dropdownButtonRefs.current[key];
    if (button) {
      const rect = button.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownApproxHeight = 350; // increased from 280 to account for search bar and max-h-64

      const isUp = spaceBelow < dropdownApproxHeight && spaceAbove > spaceBelow;

      // Calculate max height based on direction to prevent touching the window edges
      const availableSpace = isUp ? spaceAbove : spaceBelow;
      // Subtract padding (e.g., 20px) to ensure it doesn't touch the exact edge
      const maxHeight = Math.max(150, availableSpace - 20);

      setDropdownRect({
        top: rect.bottom,
        bottomPos: window.innerHeight - rect.top,
        left: rect.left,
        width: rect.width,
        isUp,
        maxHeight
      });
    }
  }, []);

  // Update position on window resize or scroll
  useEffect(() => {
    if (!openDropdown) return;

    const handleUpdate = () => updateDropdownPosition(openDropdown);

    window.addEventListener("resize", handleUpdate);
    // Use capture: true to catch scrolls in any container
    window.addEventListener("scroll", handleUpdate, true);

    return () => {
      window.removeEventListener("resize", handleUpdate);
      window.removeEventListener("scroll", handleUpdate, true);
    };
  }, [openDropdown, updateDropdownPosition]);

  const [savedFilters, setSavedFilters] = useState([]);
  const [isLoadingSavedFilters, setIsLoadingSavedFilters] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveFilterName, setSaveFilterName] = useState(
    currentFilters.saveFilterName || "",
  );
  const [saveFilterNote, setSaveFilterNote] = useState(
    currentFilters.saveFilterNote || "",
  );
  const [isSaving, setIsSaving] = useState(false);
  const [editingFilterGroup, setEditingFilterGroup] = useState(
    currentFilters.editingFilterGroup || null,
  );
  const [appliedSavedFilters, setAppliedSavedFilters] = useState(
    currentFilters.appliedSavedFilters || [],
  );
  const [groupedSavedFilters, setGroupedSavedFilters] = useState({});
  const [savedFiltersGroupCount, setSavedFiltersGroupCount] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [filterToDelete, setFilterToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeModalGroupId, setActiveModalGroupId] = useState(null);
  const [backupFieldGroups, setBackupFieldGroups] = useState(null);
  const [backupAppliedSavedFilters, setBackupAppliedSavedFilters] =
    useState(null);

  // Synchronize appliedSavedFilters with parent if changed (with lookup from loaded templates)
  useEffect(() => {
    if (currentFilters.appliedSavedFilters && Object.keys(groupedSavedFilters).length > 0) {
      const syncedTemplates = currentFilters.appliedSavedFilters.map(applied => {
        const gId = applied.id || applied.Id;
        const match = Object.values(groupedSavedFilters).find(g => g.id === gId);
        return match || applied;
      });

      const currentIds = appliedSavedFilters.map(f => f.id || f.Id).join(",");
      const syncedIds = syncedTemplates.map(f => f.id || f.Id).join(",");
      if (currentIds !== syncedIds) {
        setAppliedSavedFilters(syncedTemplates);
      }
    } else if (currentFilters.appliedSavedFilters) {
      const currentIds = appliedSavedFilters.map(f => f.id || f.Id).join(",");
      const propIds = currentFilters.appliedSavedFilters.map(f => f.id || f.Id).join(",");
      if (currentIds !== propIds) {
        setAppliedSavedFilters(currentFilters.appliedSavedFilters);
      }
    }
  }, [currentFilters.appliedSavedFilters, groupedSavedFilters]);

  // Synchronize fieldGroups editor state when parent groupedFilters prop changes
  useEffect(() => {
    const activeGroupedFilters = currentFilters?.draftGroupedFilters || currentFilters?.groupedFilters;
    
    if (!activeGroupedFilters) {
      if (fieldGroups.length > 0 && fieldGroups[0].fieldId) {
        setFieldGroups([
          {
            id: generateId(),
            fieldId: "",
            logic: "AND",
            conditions: [{ id: generateId(), operator: "=", value: "" }],
          }
        ]);
      }
      return;
    }

    const groupsRaw = activeGroupedFilters.Groups || activeGroupedFilters.groups || [];
    const groups = Array.isArray(groupsRaw) ? groupsRaw : [groupsRaw];
    if (groups.length === 0) return;

    const currentRulesStr = JSON.stringify(fieldGroups.map(g => ({
      fieldId: g.fieldId,
      logic: g.logic,
      conditions: g.conditions.map(c => ({ operator: c.operator, value: c.value }))
    })));

    const propRulesStr = JSON.stringify(groups.map((group) => {
      const groupRulesRaw = group.Rules || group.rules || [];
      const groupRules = Array.isArray(groupRulesRaw) ? groupRulesRaw : [groupRulesRaw];
      const fieldId = groupRules[0]?.field || groupRules[0]?.FieldId || groupRules[0]?.Field || groupRules[0]?.MasterFieldId || "";
      return {
        fieldId,
        logic: group.Logic || "OR",
        conditions: groupRules.map(rule => ({
          operator: rule.Operator || rule.operator || "=",
          value: rule.Value || rule.value || ""
        }))
      };
    }));

    if (currentRulesStr !== propRulesStr) {
      const loadedGroupLogic = activeGroupedFilters.GroupLogic || "AND";
      setGroupLogic(loadedGroupLogic);

      const loadedFieldGroups = groups.map((group, index) => {
        const groupRulesRaw = group.Rules || group.rules || [];
        const groupRules = Array.isArray(groupRulesRaw) ? groupRulesRaw : [groupRulesRaw];
        const fieldId = groupRules[0]?.field || groupRules[0]?.FieldId || groupRules[0]?.Field || groupRules[0]?.MasterFieldId || "";
        const template = appliedSavedFilters.find((t) =>
          t.fields?.some((f) => f.FieldId === fieldId)
        );

        return {
          id: generateId(),
          fieldId,
          templateId: template?.id || template?.Id,
          connector: index > 0 ? loadedGroupLogic : undefined,
          logic: group.Logic || "OR",
          conditions: groupRules.map((rule) => ({
            id: generateId(),
            operator: rule.Operator || rule.operator || "=",
            value: rule.Value || rule.value || "",
          })),
        };
      });
      setFieldGroups(loadedFieldGroups);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilters?.groupedFilters, currentFilters?.draftGroupedFilters]);

  const resetFilters = useCallback(() => {
    setFieldGroups([]);
    setGroupLogic("AND");
    setAppliedSavedFilters([]);
    onApplyFilters({
      groupedFilters: null,
      appliedSavedFilters: [],
      shouldClearQuickFilters: true,
      editingFilterGroup: null,
      saveFilterName: "",
      saveFilterNote: "",
      draftGroupedFilters: null,
      draftQuickFilters: null,
    });
  }, [onApplyFilters]);

  useEffect(() => {
    const fetchData = async () => {
      if (masterFields.length > 0 && Object.keys(operators).length > 0) return;
      setIsLoadingFields(true);
      try {
        const masterFieldsResult = entityType === "job"
          ? await getUsedJobMasterFields()
          : await getMasterFields();
        if (masterFieldsResult.success) {
          const categories = masterFieldsResult.data || [];
          const availableFields = [];

          categories.forEach((category) => {
            if (category.MasterFields && Array.isArray(category.MasterFields)) {
              category.MasterFields.forEach((field) => {
                if (field.IsActive) {
                  availableFields.push({
                    id: field.Id,
                    fieldName: field.FieldName,
                    displayName: field.FieldLabel,
                    categoryName: category.CategoryName,
                    fieldTypeId: field.FieldTypeId,
                    typeName: field.TypeName,
                    htmlInputType: field.HtmlInputType,
                    options: field.MasterFormFieldOptions || [],
                  });
                }
              });
            }
          });

          setMasterFields(availableFields);
        }

        const operatorsResult = await getAdvancedFilterFieldsWithOperators(entityType === "job");
        if (operatorsResult.success) {
          setOperators(operatorsResult.data.operators || {});
          setDropdowns(operatorsResult.data.dropdowns || {});
        }
      } catch (error) {
        console.error("Error fetching filter data:", error);
      } finally {
        setIsLoadingFields(false);
      }
    };

    fetchData();
  }, [masterFields.length]);

  const fetchSavedFilters = useCallback(async () => {
    if (Object.keys(groupedSavedFilters).length === 0) {
      setIsLoadingSavedFilters(true);
    }
    try {
      const result = await getSavedFilters(entityType === "job");
      if (result.success && result.data) {
        const groups = (result.data || []).reduce((acc, current) => {
          const groupName = current.Name || "Unnamed Filter";

          let note = current.Note || "";
          let rawQuickFilters = current.QuickFilters || current.QuickFilter; // Fallback if backend does return it
          let parsedQuickFilters = {};
          if (typeof rawQuickFilters === 'string') {
            try { parsedQuickFilters = JSON.parse(rawQuickFilters); } catch (e) { }
          } else if (typeof rawQuickFilters === 'object' && rawQuickFilters !== null) {
            parsedQuickFilters = rawQuickFilters;
          }

          const garbageKeys = [
            "draftGroupedFilters",
            "draftQuickFilters",
            "saveFilterName",
            "saveFilterNote",
            "editingFilterGroup",
            "isApplyingTemplate",
            "clearQuickFilterKeys",
            "shouldClearQuickFilters",
            "keepOpen",
          ];
          if (parsedQuickFilters && typeof parsedQuickFilters === 'object') {
            garbageKeys.forEach(k => delete parsedQuickFilters[k]);
          }

          let QuickFilters = parsedQuickFilters;
          if (entityType === "job") {
            const uiQuickFilters = {};
            Object.keys(parsedQuickFilters).forEach(k => {
              if (k === 'statusIds') {
                try {
                  uiQuickFilters.status = JSON.parse(parsedQuickFilters[k]);
                } catch (e) {
                  uiQuickFilters.status = parsedQuickFilters[k];
                }
              } else if (k === 'fromDate' || k === 'toDate') {
                uiQuickFilters.createdDate = uiQuickFilters.createdDate || {};
                uiQuickFilters.createdDate[k] = parsedQuickFilters[k];
              } else if (k === 'modifiedFromDate' || k === 'modifiedToDate') {
                uiQuickFilters.modifiedDate = uiQuickFilters.modifiedDate || {};
                uiQuickFilters.modifiedDate[k === 'modifiedFromDate' ? 'fromDate' : 'toDate'] = parsedQuickFilters[k];
              } else if (k === 'hideArchiveJobs') {
                uiQuickFilters.hideWM = parsedQuickFilters[k];
              } else {
                uiQuickFilters[k] = parsedQuickFilters[k];
              }
            });
            QuickFilters = uiQuickFilters;
          }

          acc[groupName] = {
            id: current.Id,
            name: groupName,
            note: note,
            fields: current.Fields || [],
            QuickFilters: QuickFilters,
            createdAt: current.CreatedAt,
            updatedAt: current.UpdatedAt,
          };
          return acc;
        }, {});
        setGroupedSavedFilters(groups);
        setSavedFiltersGroupCount(result.groupCount || 0);
        onSavedCountChange?.(result.groupCount || 0);
      }
    } catch (error) {
      console.error("Error fetching saved filters:", error);
    } finally {
      setIsLoadingSavedFilters(false);
    }
  }, [onSavedCountChange, entityType]);

  const [footerPortalTarget, setFooterPortalTarget] = useState(null);
  const [drawerPortalTarget, setDrawerPortalTarget] = useState(null);

  useEffect(() => {
    const target = document.getElementById("drawer-footer-portal");
    if (target) {
      setFooterPortalTarget(target);
    } else {
      const timer = setTimeout(() => {
        setFooterPortalTarget(document.getElementById("drawer-footer-portal"));
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      const target = containerRef.current.closest("#lead-filters-container");
      if (target) {
        setDrawerPortalTarget(target);
      } else {
        // Fallback to document lookup if closest fails for some reason
        setDrawerPortalTarget(
          document.getElementById("lead-filters-container"),
        );
      }
    }
  }, []);

  useEffect(() => {
    fetchSavedFilters();
  }, [fetchSavedFilters]);



  const addFieldGroup = useCallback(() => {
    setBackupFieldGroups(fieldGroups);
    setBackupAppliedSavedFilters(appliedSavedFilters);
    const newId = generateId();
    setFieldGroups((prev) => [
      {
        id: newId,
        fieldId: "",
        logic: "AND",
        conditions: [
          {
            id: generateId(),
            operator: "",
            value: "",
          },
        ],
      },
      ...prev,
    ]);
    setActiveModalGroupId(newId);
  }, [groupLogic, fieldGroups, appliedSavedFilters]);

  const handleCancelGroupEdit = useCallback(() => {
    if (backupFieldGroups !== null) {
      setFieldGroups(backupFieldGroups);
    }
    if (backupAppliedSavedFilters !== null) {
      setAppliedSavedFilters(backupAppliedSavedFilters);
    }
    setActiveModalGroupId(null);
    setBackupFieldGroups(null);
    setBackupAppliedSavedFilters(null);
  }, [backupFieldGroups, backupAppliedSavedFilters]);

  const handleDoneGroupEdit = useCallback(() => {
    setActiveModalGroupId(null);
    setBackupFieldGroups(null);
    setBackupAppliedSavedFilters(null);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingFilterGroup(null);
    setSaveFilterName("");
    setSaveFilterNote("");

    const resetGroups = [
      {
        id: generateId(),
        fieldId: "",
        logic: "AND",
        conditions: [{ id: generateId(), operator: "=", value: "" }],
      },
    ];
    setFieldGroups(resetGroups);
    setAppliedSavedFilters([]);

    onApplyFilters?.({
      groupedFilters: null,
      draftGroupedFilters: null,
      draftQuickFilters: null,
      appliedSavedFilters: [],
      editingFilterGroup: null,
      saveFilterName: "",
      saveFilterNote: "",
      keepOpen: true,
      shouldClearQuickFilters: true,
    });

    toast.info("Edit cancelled", { duration: 2000 });
  }, [onApplyFilters]);

  const getOperatorLabel = useCallback(
    (val) => {
      return (
        Object.keys(operators).find((key) => operators[key] === val) || val
      );
    },
    [operators],
  );

  const applySilently = useCallback(
    (newGroups, newAppliedFilters, clearQuickFilterKeys = []) => {
      const validGroups = newGroups
        .filter((group) => group.fieldId)
        .map((group) => {
          const fieldInfo = masterFields.find((f) => String(f.id).toLowerCase() === String(group.fieldId).toLowerCase());
          const rules = group.conditions
            .filter(
              (condition) =>
                condition.operator &&
                (condition.value ||
                  ["IS NULL", "IS NOT NULL"].includes(condition.operator)),
            )
            .map((condition) => ({
              field: group.fieldId,
              FieldId: group.fieldId,
              MasterFieldId: group.fieldId,
              FieldName: fieldInfo?.fieldName,
              Operator: condition.operator,
              Value: Array.isArray(condition.value)
                ? condition.value.join(",")
                : condition.value,
            }));
          return {
            Logic: group.logic || "OR",
            Rules: rules,
            GroupName: fieldInfo?.displayName || "Advanced Group",
          };
        })
        .filter((group) => group.Rules && group.Rules.length > 0);

      let groupedFilters = null;
      if (validGroups.length > 0) {
        groupedFilters = { GroupLogic: groupLogic, Groups: validGroups };
      }

      if (onApplyFilters) {
        onApplyFilters({
          groupedFilters,
          appliedSavedFilters: newAppliedFilters,
          clearQuickFilterKeys,
          keepOpen: true,
        });
      }
    },
    [groupLogic, masterFields, onApplyFilters],
  );

  const removeFieldGroup = useCallback(
    (groupId) => {
      let newGroups = [];
      let newAppliedFilters = appliedSavedFilters;
      let clearQuickFilterKeys = [];

      setFieldGroups((prev) => {
        const groupToRemove = prev.find((g) => g.id === groupId);
        if (groupToRemove?.templateId) {
          const templateToRemove = appliedSavedFilters.find(
            (t) => t.id === groupToRemove.templateId,
          );
          // Do NOT clear quick filters when an advanced filter group is deleted
          // if (templateToRemove) {
          //   clearQuickFilterKeys = Object.keys(
          //     templateToRemove.QuickFilters || templateToRemove.QuickFilter || {},
          //   );
          // }
          newAppliedFilters = appliedSavedFilters.filter(
            (t) => t.id !== groupToRemove.templateId,
          );
          setAppliedSavedFilters(newAppliedFilters);
        }
        newGroups = prev.filter((g) => g.id !== groupId);

        // We apply silently right after computing the new state
        // if (!editingFilterGroup) {
        //   applySilently(newGroups, newAppliedFilters, clearQuickFilterKeys);
        // }

        return newGroups;
      });
    },
    [appliedSavedFilters, applySilently, editingFilterGroup],
  );

  const updateFieldGroup = useCallback((groupIndex, fieldId) => {
    setFieldGroups((prev) => {
      const updated = [...prev];
      if (updated[groupIndex]?.templateId) {
        setAppliedSavedFilters((current) =>
          current.filter((t) => t.id !== updated[groupIndex].templateId),
        );
      }
      updated[groupIndex] = {
        ...updated[groupIndex],
        fieldId,
        templateId: null,
        logic: "AND",
        conditions: [
          {
            id: generateId(),
            operator: "",
            value: "",
          },
        ],
      };
      return updated;
    });

    setTimeout(() => {
      const key = `${groupIndex}-0`;
      valueRefs.current[key]?.focus();
    }, 100);
  }, []);

  const addConditionToGroup = useCallback((groupIndex) => {
    setFieldGroups((prev) => {
      const updated = [...prev];
      if (updated[groupIndex]?.templateId) {
        setAppliedSavedFilters((current) =>
          current.filter((t) => t.id !== updated[groupIndex].templateId),
        );
        updated[groupIndex].templateId = null;
      }
      updated[groupIndex] = {
        ...updated[groupIndex],
        conditions: [
          {
            id: generateId(),
            operator: "",
            value: "",
          },
          ...updated[groupIndex].conditions,
        ],
      };
      return updated;
    });
  }, []);

  const removeConditionFromGroup = useCallback(
    (groupIndex, conditionIndex) => {
      let newAppliedFilters = appliedSavedFilters;

      setFieldGroups((prev) => {
        const updated = [...prev];
        const group = updated[groupIndex];

        if (group.conditions.length > 1) {
          const newConditions = group.conditions.filter(
            (_, index) => index !== conditionIndex,
          );

          updated[groupIndex] = {
            ...group,
            conditions: newConditions,
          };

          if (group.templateId) {
            newAppliedFilters = appliedSavedFilters.filter(
              (t) => t.id !== group.templateId,
            );
            setAppliedSavedFilters(newAppliedFilters);
            updated[groupIndex].templateId = null;
          }
        }

        // if (!editingFilterGroup) {
        //   applySilently(updated, newAppliedFilters);
        // }
        return updated;
      });
    },
    [appliedSavedFilters, applySilently, editingFilterGroup],
  );

  const updateCondition = useCallback(
    (groupIndex, conditionIndex, field, value) => {
      setFieldGroups((prev) => {
        const updated = [...prev];
        const group = updated[groupIndex];
        const newConditions = [...group.conditions];

        newConditions[conditionIndex] = {
          ...newConditions[conditionIndex],
          [field]: value,
        };

        updated[groupIndex] = {
          ...group,
          conditions: newConditions,
        };

        if (updated[groupIndex].templateId) {
          setAppliedSavedFilters((current) =>
            current.filter((t) => t.id !== updated[groupIndex].templateId),
          );
          updated[groupIndex].templateId = null;
        }
        return updated;
      });
    },
    [],
  );

  const handleApplyFilters = useCallback(async () => {
    setIsApplying(true);
    try {
      const validGroups = fieldGroups
        .filter((group) => group.fieldId)
        .map((group) => {
          const fieldInfo = masterFields.find((f) => String(f.id).toLowerCase() === String(group.fieldId).toLowerCase());
          const rules = group.conditions
            .filter(
              (condition) =>
                condition.operator &&
                (condition.value ||
                  ["IS NULL", "IS NOT NULL"].includes(condition.operator)),
            )
            .map((condition) => ({
              field: group.fieldId,
              FieldId: group.fieldId,
              MasterFieldId: group.fieldId,
              FieldName: fieldInfo?.fieldName,
              Operator: condition.operator,
              Value: Array.isArray(condition.value)
                ? condition.value.join(",")
                : condition.value,
            }));

          return {
            Logic: "OR",
            Rules: rules,
            GroupName: fieldInfo?.displayName || "Advanced Group",
          };
        })
        .filter((group) => group.Rules && group.Rules.length > 0);

      let groupedFilters = null;
      if (validGroups.length > 0) {
        groupedFilters = {
          GroupLogic: "AND",
          Groups: validGroups,
        };
      }

      if (onApplyFilters) {
        onApplyFilters({
          groupedFilters,
          appliedSavedFilters,
          editingFilterGroup,
          saveFilterName,
          saveFilterNote,
          draftGroupedFilters: null,
          draftQuickFilters: null,
        });
      }
      toast.success("Filters applied successfully");
    } catch (error) {
      console.error("Error applying filters:", error);
    } finally {
      setIsApplying(false);
    }
  }, [fieldGroups, masterFields, appliedSavedFilters, onApplyFilters]);

  const buildFiltersJson = (name, note) => {
    const fields = fieldGroups
      .filter((group) => group.fieldId)
      .map((group) => {
        const conditions = group.conditions
          .filter(
            (c) =>
              c.operator &&
              (c.value || ["IS NULL", "IS NOT NULL"].includes(c.operator)),
          )
          .map((c) => ({
            Operator: c.operator,
            Value: Array.isArray(c.value) ? c.value.join(",") : c.value,
          }));

        if (conditions.length === 0) return null;

        const existingField = (editingFilterGroup?.fields || []).find(
          (f) => f.FieldId === group.fieldId,
        );

        return {
          ...(existingField?.Id ? { Id: existingField.Id } : {}),
          FieldId: group.fieldId,
          Conditions: conditions,
        };
      })
      .filter((f) => f !== null);

    const quickFiltersObj = {};
    Object.keys(currentFilters || {}).forEach((k) => {
      if (
        ![
          "groupedFilters",
          "appliedSavedFilters",
          "leadview",
          "search",
          "applicantTab",
          "selectedColumns",
          "showLoader",
          "draftGroupedFilters",
          "draftQuickFilters",
          "saveFilterName",
          "saveFilterNote",
          "editingFilterGroup",
          "isApplyingTemplate",
          "clearQuickFilterKeys",
          "shouldClearQuickFilters",
          "keepOpen",
        ].includes(k) &&
        currentFilters[k] !== null &&
        currentFilters[k] !== undefined &&
        currentFilters[k] !== "" &&
        currentFilters[k] !== false &&
        (Array.isArray(currentFilters[k]) ? currentFilters[k].length > 0 : true)
      ) {
        if (entityType === "job") {
          if (k === "status" || k === "statusIds") {
            const val = currentFilters.status !== undefined ? currentFilters.status : currentFilters.statusIds;
            const arr = Array.isArray(val) ? val : [];
            quickFiltersObj["statusIds"] = `[${arr.join(",")}]`;
          } else if (k === "createdDate" && currentFilters[k]) {
            if (currentFilters[k].fromDate) quickFiltersObj["fromDate"] = currentFilters[k].fromDate;
            if (currentFilters[k].toDate) quickFiltersObj["toDate"] = currentFilters[k].toDate;
          } else if (k === "modifiedDate" && currentFilters[k]) {
            if (currentFilters[k].fromDate) quickFiltersObj["modifiedFromDate"] = currentFilters[k].fromDate;
            if (currentFilters[k].toDate) quickFiltersObj["modifiedToDate"] = currentFilters[k].toDate;
          } else if (k === "hideWM") {
            quickFiltersObj["hideArchiveJobs"] = currentFilters[k];
          } else if (!["fromDate", "toDate", "modifiedFromDate", "modifiedToDate", "statusIds", "hideArchiveJobs"].includes(k)) {
            quickFiltersObj[k] = currentFilters[k];
          }
        } else {
          quickFiltersObj[k] = currentFilters[k];
        }
      }
    });
    const hasActiveQuick = Object.keys(quickFiltersObj).length > 0;
    if (fields.length === 0 && !hasActiveQuick) return [];
    const finalNote = note || "";

    return [
      {
        ...(editingFilterGroup?.id ? { Id: editingFilterGroup.id } : {}),
        Name: name,
        Note: finalNote || null,
        QuickFilter: hasActiveQuick ? quickFiltersObj : null,
        Fields: fields,
      },
    ];
  };

  const handleSaveFilters = async () => {
    if (!saveFilterName.trim()) {
      toast.error("Filter name is required.");
      return;
    }
    const filtersArray = buildFiltersJson(
      saveFilterName.trim(),
      saveFilterNote.trim(),
    );
    if (filtersArray.length === 0) {
      toast.error(
        "Please configure at least one filter group with a value before saving.",
      );
      return;
    }
    setIsSaving(true);
    try {
      const result = await saveAdvancedFilterFields(filtersArray, entityType === "job");
      if (result.success) {
        const isUpdate = !!editingFilterGroup;
        toast.success(
          isUpdate
            ? `"${saveFilterName}" updated successfully!`
            : result.message || "Filters saved successfully!",
        );
        if (isUpdate) {
          setAppliedSavedFilters((prev) =>
            prev.map((f) =>
              f.id === editingFilterGroup.id
                ? { ...f, name: saveFilterName.trim() }
                : f,
            ),
          );
        }

        setShowSaveModal(false);
        setEditingFilterGroup(null);
        setSaveFilterName("");
        setSaveFilterNote("");

        // Reset the editor to a clean state
        const resetGroups = [
          {
            id: generateId(),
            fieldId: "",
            logic: "AND",
            conditions: [{ id: generateId(), operator: "=", value: "" }],
          },
        ];
        setFieldGroups(resetGroups);
        setAppliedSavedFilters([]);

        // Update the parent state silently to reflect the cleared editor
        onApplyFilters?.({
          groupedFilters: null,
          draftGroupedFilters: null,
          draftQuickFilters: null,
          appliedSavedFilters: [],
          editingFilterGroup: null,
          saveFilterName: "",
          saveFilterNote: "",
          keepOpen: true,
          shouldClearQuickFilters: true,
        });

        fetchSavedFilters();

        if (activeTab === "filter") onTabChange("saved");
      } else {
        toast.error(
          result.error || "Failed to save filters. Please try again.",
        );
      }
    } catch (err) {
      toast.error("An unexpected error occurred.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleApplySavedFilter = (group) => {
    if (appliedSavedFilters.some((f) => f.id === group.id)) {
      toast.info(`Filter "${group.name}" is already applied`);
      return;
    }

    const newGroups = (group.fields || []).map((f) => ({
      id: generateId(),
      templateId: group.id,
      fieldId: f.FieldId,
      logic: "AND",
      conditions: (f.Conditions || []).map((cond) => ({
        id: generateId(),
        operator: cond.Operator || "=",
        value: cond.Value || "",
      })),
    }));

    const updatedTemplates = [group];
    setAppliedSavedFilters(updatedTemplates);

    setFieldGroups(() => {
      const updated = newGroups.length > 0 ? newGroups : [
        {
          id: generateId(),
          fieldId: "",
          logic: "AND",
          conditions: [{ id: generateId(), operator: "=", value: "" }],
        }
      ];

      onTabChange("filter");

      // Compute grouped filters for immediate application
      const validGroups = updated
        .filter((g) => g.fieldId)
        .map((g) => {
          const fieldInfo = masterFields.find((f) => f.id === g.fieldId);
          const rules = g.conditions
            .filter(
              (rule) =>
                rule.operator &&
                (rule.value ||
                  ["IS NULL", "IS NOT NULL"].includes(rule.operator)),
            )
            .map((rule) => ({
              field: g.fieldId,
              FieldId: g.fieldId,
              MasterFieldId: g.fieldId,
              FieldName: fieldInfo?.fieldName,
              Operator: rule.operator,
              Value: Array.isArray(rule.value)
                ? rule.value.join(",")
                : rule.value,
            }));

          return {
            Logic: g.logic || "AND",
            GroupName: fieldInfo?.displayName || "Advanced Group",
            Rules: rules,
          };
        })
        .filter((g) => g.Rules.length > 0);

      const groupedFilters =
        validGroups.length > 0
          ? {
            Groups: validGroups,
          }
          : null;

      // Apply filters immediately to main state
      onApplyFilters({
        groupedFilters,
        appliedSavedFilters: updatedTemplates,
        quickFilters: group.QuickFilters,
        keepOpen: true,
        shouldClearQuickFilters: true,
      });

      toast.success(`Template "${group.name}" applied.`);

      return updated;
    });
  };

  const handleRemoveAppliedTemplate = (templateId, templateName) => {
    const templateToRemove = appliedSavedFilters.find(
      (f) => f.id === templateId,
    );

    const updatedTemplates = appliedSavedFilters.filter(
      (f) => f.id !== templateId,
    );
    setAppliedSavedFilters(updatedTemplates);

    setFieldGroups((prev) => {
      const remaining = prev.filter((g) => g.templateId !== templateId);
      const finalGroups =
        remaining.length === 0
          ? [
            {
              id: generateId(),
              fieldId: "",
              logic: "AND",
              conditions: [{ id: generateId(), operator: "=", value: "" }],
            },
          ]
          : remaining;

      const validGroups = finalGroups
        .filter((g) => g.fieldId)
        .map((g) => {
          const fieldInfo = masterFields.find((f) => f.id === g.fieldId);
          const rules = g.conditions
            .filter(
              (rule) =>
                rule.operator &&
                (rule.value ||
                  ["IS NULL", "IS NOT NULL"].includes(rule.operator)),
            )
            .map((rule) => ({
              field: g.fieldId,
              FieldId: g.fieldId,
              MasterFieldId: g.fieldId,
              FieldName: fieldInfo?.fieldName,
              Operator: rule.operator,
              Value: Array.isArray(rule.value)
                ? rule.value.join(",")
                : rule.value,
            }));

          return {
            Logic: g.logic || "AND",
            GroupName: fieldInfo?.displayName || "Advanced Group",
            Rules: rules,
          };
        })
        .filter((g) => g.Rules.length > 0);

      const quickFilterKeysToClear = Object.keys(
        templateToRemove?.QuickFilters || templateToRemove?.QuickFilter || {},
      );

      onApplyFilters({
        groupedFilters:
          validGroups.length > 0
            ? {
              GroupLogic: groupLogic,
              Groups: validGroups,
            }
            : null,
        appliedSavedFilters: updatedTemplates,
        clearQuickFilterKeys: quickFilterKeysToClear,
        keepOpen: true,
      });

      return finalGroups;
    });

    toast.info(`Template "${templateName}" removed`);

    if (editingFilterGroup?.id === templateId) {
      setEditingFilterGroup(null);
      setSaveFilterName("");
      setSaveFilterNote("");
    }
  };

  const handleEditSavedFilter = (group) => {
    const newFieldGroups = (group.fields || []).map((f) => ({
      id: generateId(),
      templateId: group.id,
      fieldId: f.FieldId,
      logic: "AND",
      conditions: (f.Conditions || []).map((cond) => ({
        id: generateId(),
        operator: cond.Operator || "=",
        value: cond.Value || "",
      })),
    }));

    setFieldGroups(
      newFieldGroups.length > 0
        ? newFieldGroups
        : [
          {
            id: generateId(),
            fieldId: "",
            logic: "AND",
            conditions: [{ id: generateId(), operator: "=", value: "" }],
          },
        ],
    );

    setEditingFilterGroup(group);
    setSaveFilterName(group.name);
    setSaveFilterNote(group.note || "");

    // Compute grouped filters for immediate syncing with parent
    const validGroups = newFieldGroups
      .filter((g) => g.fieldId)
      .map((g) => {
        const fieldInfo = masterFields.find((f) => f.id === g.fieldId);
        const rules = g.conditions
          .filter(
            (rule) =>
              rule.operator &&
              (rule.value ||
                ["IS NULL", "IS NOT NULL"].includes(rule.operator)),
          )
          .map((rule) => ({
            field: g.fieldId,
            FieldId: g.fieldId,
            MasterFieldId: g.fieldId,
            FieldName: fieldInfo?.fieldName,
            Operator: rule.operator,
            Value: Array.isArray(rule.value)
              ? rule.value.join(",")
              : rule.value,
          }));

        return {
          Logic: g.logic || "AND",
          GroupName: fieldInfo?.displayName || "Advanced Group",
          Rules: rules,
        };
      })
      .filter((g) => g.Rules.length > 0);

    const groupedFilters =
      validGroups.length > 0
        ? {
          Groups: validGroups,
        }
        : null;

    // Apply filters to parent state as DRAFT so it doesn't trigger listing refresh, but persists for drawer
    onApplyFilters?.({
      draftGroupedFilters: groupedFilters,
      draftQuickFilters: group.QuickFilters,
      appliedSavedFilters: appliedSavedFilters,
      keepOpen: true,
      editingFilterGroup: group,
      saveFilterName: group.name,
      saveFilterNote: group.note || "",
      shouldClearQuickFilters: true,
      isStartingEdit: true,
    });

    onTabChange?.("filter");
    toast.info(
      `Editing "${group.name}" — modify and click Save Filters to update`,
      { duration: 2000 },
    );
  };

  const handleDeleteSavedFilter = (group) => {
    if (!group.id) return;
    setFilterToDelete(group);
    setShowDeleteConfirm(true);
  };

  const onConfirmDelete = async () => {
    if (!filterToDelete?.id) return;
    setIsDeleting(true);
    try {
      const result = await deleteSavedFilter(filterToDelete.id);
      if (result.success) {
        toast.success(`Filter "${filterToDelete.name}" deleted successfully`, {
          duration: 2000,
        });

        if (appliedSavedFilters.some((f) => f.id === filterToDelete.id)) {
          handleRemoveAppliedTemplate(filterToDelete.id, filterToDelete.name);
        }

        setShowDeleteConfirm(false);
        setFilterToDelete(null);
        fetchSavedFilters();
      } else {
        toast.error(result.error || "Failed to delete filter");
      }
    } catch (error) {
      console.error("Error deleting filter:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getOperatorsForField = () => {
    return Object.entries(operators).map(([key, value]) => ({
      label: key,
      value: value,
    }));
  };

  const getFieldOptions = (fieldId) => {
    const field = masterFields.find((f) => f.id === fieldId);
    if (!field) return [];

    let options = [];

    // Special handling for tags field — always use ID as value, not label
    const fieldKeyLower = field.fieldName?.toLowerCase();
    if (fieldKeyLower === "tags" || fieldKeyLower === "tag") {
      if (tagsData && tagsData.length > 0) {
        // Use tagsData prop (from LeadFilters cache) for accurate Id-based options
        options = tagsData.map((tag) => ({
          label: tag.TagName || "",
          value: tag.Id,
          id: tag.Id,
        }));
      } else if (
        dropdowns[field.fieldName] &&
        Array.isArray(dropdowns[field.fieldName])
      ) {
        // Fallback: use dropdown data but try to map label->Id if possible
        options = dropdowns[field.fieldName].map((opt) => ({
          label: opt.label,
          value: opt.value,
          id: opt.value,
        }));
      }
    } else if (field.options && field.options.length > 0) {
      options = field.options.map((opt) => ({
        label: opt.OptionLabel,
        value: opt.OptionValue,
        id: opt.Id,
      }));
    } else {
      const fieldKey = field.fieldName;
      if (dropdowns[fieldKey] && Array.isArray(dropdowns[fieldKey])) {
        options = dropdowns[fieldKey].map((opt) => ({
          label: opt.label,
          value: opt.value,
          id: opt.value,
        }));
      }
    }

    if (options.length === 0) return [];

    // Deduplicate by label and value to prevent duplicates in UI
    const seen = new Set();
    const uniqueOptions = options.filter((opt) => {
      const key = `${opt.label}-${opt.value}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (fieldKeyLower?.includes("stage")) {
      return uniqueOptions;
    }

    return uniqueOptions.sort((a, b) =>
      (a.label || "").localeCompare(b.label || ""),
    );
  };

  const getGroupedFieldOptions = (currentGroupId) => {
    const categoryMap = new Map();

    masterFields.forEach((field) => {
      const category = field.categoryName || "Other";
      if (!categoryMap.has(category)) {
        categoryMap.set(category, []);
      }
      categoryMap.get(category).push({
        label: field.displayName,
        value: field.id,
      });
    });
    return Array.from(categoryMap.entries())
      .sort(([catA], [catB]) => catA.localeCompare(catB))
      .map(([category, items]) => ({
        category,
        items: items.sort((a, b) =>
          (a.label || "").localeCompare(b.label || ""),
        ),
      }));
  };

  const renderCustomDropdown = (
    dropdownKey,
    options,
    selectedValue,
    onSelect,
    placeholder,
    compact = false,
    enableSearch = false,
    grouped = false,
  ) => {
    const isOpen = openDropdown === dropdownKey;
    const searchTerm = searchTerms[dropdownKey] || "";

    let selectedOption;

    if (grouped && Array.isArray(options)) {
      options.forEach((group) => {
        if (group.items) {
          const found = group.items.find((opt) => opt.value === selectedValue);
          if (found) selectedOption = found;
        }
      });
    } else {
      selectedOption = options?.find((opt) => opt.value === selectedValue);
    }

    let filteredOptions = options || [];
    if (enableSearch && searchTerm) {
      if (grouped) {
        const lowerSearchTerm = searchTerm.toLowerCase();
        filteredOptions = options
          .map((group) => ({
            ...group,
            items: group.category?.toLowerCase().includes(lowerSearchTerm)
              ? group.items
              : group.items?.filter((opt) =>
                opt.label?.toLowerCase().includes(lowerSearchTerm),
              ) || [],
          }))
          .filter((group) => group.items.length > 0);
      } else {
        filteredOptions =
          options?.filter((opt) =>
            opt.label?.toLowerCase().includes(searchTerm.toLowerCase()),
          ) || [];
      }
    }

    return (
      <div className="relative">
        <button
          type="button"
          onClick={(e) => {
            const newIsOpen = !isOpen;
            if (newIsOpen) {
              setOpenDropdown(dropdownKey);
              // Small delay to ensure the button is in the ref and layout is stable
              setTimeout(() => updateDropdownPosition(dropdownKey), 0);
              if (enableSearch) {
                setTimeout(
                  () => searchInputRefs.current[dropdownKey]?.focus(),
                  0,
                );
              }
            } else {
              setOpenDropdown(null);
            }
          }}
          ref={(el) => (dropdownButtonRefs.current[dropdownKey] = el)}
          className={clsx(
            "flex w-full items-center justify-between rounded-lg border bg-white text-left transition-all focus:ring-2 focus:outline-none dark:bg-gray-700",
            compact
              ? "focus:border-primary-500 focus:ring-primary-500/20 border-gray-300 px-2 py-1 text-[13px] hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500"
              : "focus:border-primary-500 focus:ring-primary-500/30 border-gray-300 px-3.5 py-2.5 text-sm shadow-sm hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500",
            selectedOption
              ? "text-gray-900 dark:text-gray-100"
              : "text-gray-500 dark:text-gray-400",
          )}
        >
          <span className="truncate">
            {selectedOption?.label || placeholder}
          </span>
          <ChevronDownIcon
            className={clsx(
              "h-4 w-4 text-gray-400 transition-transform",
              isOpen && "rotate-180",
            )}
          />
        </button>

        {isOpen && typeof document !== 'undefined' && createPortal(
          <div
            className={clsx("fixed z-[100000] rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-600 dark:bg-gray-700 flex flex-col", dropdownRect?.isUp ? "mb-1" : "mt-1")}
            style={{
              ...(dropdownRect?.isUp
                ? { bottom: dropdownRect.bottomPos }
                : { top: dropdownRect?.top || 0 }),
              left: dropdownRect?.left || 0,
              width: dropdownRect?.width || 'auto',
              maxHeight: dropdownRect?.maxHeight ? `${dropdownRect.maxHeight}px` : 'auto'
            }}
          >
            {enableSearch && (
              <div className="border-b border-gray-200 p-2.5 dark:border-gray-600 shrink-0">
                <input
                  ref={(el) => (searchInputRefs.current[dropdownKey] = el)}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerms((prev) => ({ ...prev, [dropdownKey]: e.target.value }))}
                  placeholder="Search fields..."
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <div className="overflow-y-auto flex-1" style={{ maxHeight: dropdownRect?.maxHeight ? 'none' : '16rem' }}>
              {filteredOptions.length > 0 ? (
                grouped ? (
                  filteredOptions.map((group, gIdx) => (
                    <div key={group.category || gIdx}>
                      {group.category && (
                        <div className="sticky top-0 z-[10] border-b border-gray-200 bg-gray-50 px-3.5 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400">
                          {group.category}
                        </div>
                      )}
                      {group.items.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            onSelect(opt.value);
                            setOpenDropdown(null);
                            setSearchTerms((prev) => ({ ...prev, [dropdownKey]: "" }));
                          }}
                          className={clsx(
                            "w-full px-3.5 py-2.5 text-left text-xs transition-colors hover:bg-primary-50 dark:hover:bg-primary-900/10 flex items-center justify-between",
                            selectedValue === opt.value
                              ? "bg-primary-50 font-semibold text-primary-700 dark:bg-primary-900/20 dark:text-primary-300"
                              : "text-gray-700 dark:text-gray-300"
                          )}
                        >
                          <span>{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  ))
                ) : (
                  filteredOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        onSelect(opt.value);
                        setOpenDropdown(null);
                        setSearchTerms((prev) => ({ ...prev, [dropdownKey]: "" }));
                      }}
                      className={clsx(
                        "w-full px-3.5 py-2.5 text-left text-xs transition-colors hover:bg-primary-50 dark:hover:bg-primary-900/10 flex items-center justify-between",
                        selectedValue === opt.value
                          ? "bg-primary-50 font-semibold text-primary-700 dark:bg-primary-900/20 dark:text-primary-300"
                          : "text-gray-700 dark:text-gray-300"
                      )}
                    >
                      <span>{opt.label}</span>
                    </button>
                  ))
                )
              ) : (
                <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">No options found</div>
              )}
            </div>
          </div>,
          document.body,
        )}
      </div>
    );
  };

  if (isLoadingFields) {
    return (
      <div className="flex h-64 flex-col items-center justify-center">
        <div className="border-primary-100 border-t-primary-600 dark:border-primary-900/30 dark:border-t-primary-400 h-10 w-10 animate-spin rounded-full border-4"></div>
        <p className="mt-4 text-sm font-medium text-gray-500 dark:text-gray-400">
          Loading filter settings...
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex flex-1 flex-col space-y-4">
      <div className="flex-1">
        {activeTab === "filter" ? (
          <div className="space-y-4">
            {/* Add Group Action (Top) */}
            <div className="mt-2 mb-2 flex justify-center">
              <button
                onClick={addFieldGroup}
                className="border-primary-200 bg-primary-50 text-primary-600 hover:bg-primary-100 hover:text-primary-700 dark:border-primary-900/30 dark:bg-primary-900/10 dark:text-primary-400 dark:hover:bg-primary-900/20 flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-semibold transition-all hover:shadow-sm active:scale-95"
              >
                <PlusIcon className="h-4 w-4" />
                Add Filter Group
              </button>
            </div>

            {/* Filter Summary / Applied Conditions */}
            {fieldGroups.some((g) => g.fieldId) && (
              <div className="space-y-2 py-3">
                {fieldGroups
                  .filter(
                    (g) =>
                      g.fieldId &&
                      g.id !== activeModalGroupId &&
                      g.conditions.every(
                        (c) =>
                          c.operator &&
                          (c.value ||
                            ["IS NULL", "IS NOT NULL"].includes(c.operator)),
                      ),
                  )
                  .map((group, gIdx) => {
                    const field = masterFields.find(
                      (f) => String(f.id).toLowerCase() === String(group.fieldId).toLowerCase()
                    );
                    return (
                      <Fragment key={group.id}>
                        {gIdx > 0 && (
                          <div className="my-4 flex items-center gap-3">
                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent dark:via-slate-700"></div>
                            <span className="rounded-full border border-slate-200/60 bg-white/50 px-3 py-1 text-[10px] font-bold tracking-widest text-slate-400 uppercase backdrop-blur-sm dark:border-slate-700/60 dark:bg-slate-800/50 dark:text-slate-500">
                              AND
                            </span>
                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent dark:via-slate-700"></div>
                          </div>
                        )}
                        <div className="group hover:border-primary-200 relative rounded-xl border border-slate-200/60 bg-white p-3 shadow-sm transition-all hover:shadow-md dark:border-slate-700/60 dark:bg-slate-800">
                          {/* Field Header Row */}
                          <div className="mb-2 flex items-center justify-between">
                            <div className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-800 dark:text-slate-200">
                              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-slate-100 text-[10px] text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                                <AdjustmentsHorizontalIcon className="h-3.5 w-3.5" />
                              </span>
                              {field?.displayName || group.fieldId}
                            </div>

                            <div className="flex items-center gap-1.5 opacity-0 transition-opacity group-hover:opacity-100">
                              <button
                                onClick={() => {
                                  setBackupFieldGroups(fieldGroups);
                                  setBackupAppliedSavedFilters(
                                    appliedSavedFilters,
                                  );
                                  setActiveModalGroupId(group.id);
                                }}
                                className="flex h-7 w-7 items-center justify-center rounded-lg bg-orange-50 text-orange-500 transition-colors hover:bg-orange-100 dark:bg-orange-900/20"
                                title="Edit condition"
                              >
                                <PencilSquareIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => removeFieldGroup(group.id)}
                                className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-50 text-red-500 transition-colors hover:bg-red-100 dark:bg-red-900/20"
                                title="Remove filter group"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>

                          {/* Condition Rows */}
                          <div className="border-primary-100 dark:border-primary-900/30 relative ml-2.5 space-y-1.5 border-l-2 pl-3.5">
                            {group.conditions.map((cond, cIdx) => (
                              <div
                                key={cond.id}
                                className="group/cond relative flex items-center justify-between rounded-lg border border-transparent bg-slate-50/50 py-1 pl-3 transition-colors hover:border-slate-200 dark:bg-slate-900/20 dark:hover:border-slate-700"
                              >
                                {cIdx > 0 && (
                                  <div className="text-primary-500 border-primary-100 dark:border-primary-900/30 absolute top-1/2 -left-[23px] z-10 -translate-y-1/2 rounded-md border bg-white px-1.5 py-0.5 text-[9px] font-bold uppercase shadow-sm dark:bg-slate-800">
                                    OR
                                  </div>
                                )}
                                <div className="flex items-center gap-1.5 text-[12.5px] text-slate-600 dark:text-slate-300">
                                  <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-slate-500 uppercase shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                                    {getOperatorLabel(cond.operator)}
                                  </span>
                                  <span className="font-semibold break-all text-slate-800 dark:text-slate-200">
                                    {cond.value}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </Fragment>
                    );
                  })}
              </div>
            )}

            {/* Template Pills */}
            {!editingFilterGroup && appliedSavedFilters.length > 0 && (
              <div className="border-primary-100 bg-primary-50/30 dark:border-primary-900/20 dark:bg-primary-900/5 flex flex-wrap items-center gap-2 rounded-xl border p-3">
                <span className="text-primary-600 dark:text-primary-400 text-[10px] font-bold tracking-wider uppercase">
                  Templates:
                </span>
                {appliedSavedFilters.map((template) => (
                  <div
                    key={template.id}
                    className="text-primary-700 ring-primary-200 dark:text-primary-300 dark:ring-primary-800 flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-semibold shadow-sm ring-1 dark:bg-gray-800"
                  >
                    <BookmarkIcon className="h-3 w-3" />
                    {template.name}
                    <button
                      onClick={() =>
                        handleRemoveAppliedTemplate(template.id, template.name)
                      }
                      className="transition-colors hover:text-red-600"
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-1 space-y-2">
            {isLoadingSavedFilters ? (
              <div className="py-20 text-center">
                <div className="border-primary-100 border-t-primary-600 mx-auto h-10 w-10 animate-spin rounded-full border-4"></div>
              </div>
            ) : Object.keys(groupedSavedFilters).length > 0 ? (
              Object.values(groupedSavedFilters).map((group, idx) => (
                <div
                  key={group.id}
                  className="group relative overflow-hidden rounded-xl border border-slate-200/60 bg-white/60 p-3 backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md dark:border-slate-700/60 dark:bg-slate-800/60 dark:hover:border-slate-500"
                >
                  <div className="relative z-10 flex items-start justify-between">
                    <div className="flex flex-1 min-w-0 items-start gap-3 mr-2">
                      <div className="from-primary-50 to-primary-100 text-primary-600 dark:text-primary-400 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/50 bg-gradient-to-br shadow-inner dark:border-slate-600/50 dark:from-slate-800 dark:to-slate-700">
                        <BookmarkIcon className="h-4.5 w-4.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-[14px] leading-tight font-semibold tracking-tight text-slate-900 dark:text-white break-words">
                          {group.name}
                        </h4>
                        {group.note && (
                          <p className="mt-0.5 line-clamp-1 text-[12px] text-slate-500 dark:text-slate-400">
                            {group.note}
                          </p>
                        )}
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] font-medium text-slate-500 dark:text-slate-400">
                          <span className="rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-slate-600 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-300">
                            {group.fields.length} advanced
                          </span>
                          {(() => {
                            const qfCount = group.QuickFilters
                              ? Object.keys(group.QuickFilters).filter((k) => {
                                const v = group.QuickFilters[k];
                                if (Array.isArray(v)) return v.length > 0;
                                return (
                                  v !== null && v !== undefined && v !== ""
                                );
                              }).length
                              : 0;

                            return qfCount > 0 ? (
                              <span className="rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-slate-600 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-300">
                                {qfCount} quick
                              </span>
                            ) : null;
                          })()}
                          <span className="text-slate-400 dark:text-slate-500 whitespace-nowrap">
                            {new Date(group.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex shrink-0 transform gap-1.5 opacity-100 transition-all duration-300 sm:translate-x-2 sm:opacity-0 sm:group-hover:translate-x-0 sm:group-hover:opacity-100">
                      <button
                        onClick={() => handleApplySavedFilter(group)}
                        className="bg-primary-50 text-primary-600 hover:bg-primary-600 hover:shadow-primary-500/20 dark:bg-primary-900/30 dark:hover:bg-primary-600 rounded-lg p-1.5 transition-all hover:text-white hover:shadow-md"
                      >
                        <PlayIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditSavedFilter(group)}
                        className="rounded-lg bg-slate-50 p-1.5 text-slate-600 transition-all hover:bg-slate-600 hover:text-white hover:shadow-md dark:bg-slate-800/80 dark:text-slate-300 dark:hover:bg-slate-600"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteSavedFilter(group)}
                        className="rounded-lg bg-red-50 p-1.5 text-red-600 transition-all hover:bg-red-600 hover:text-white hover:shadow-md hover:shadow-red-500/20 dark:bg-red-900/20 dark:hover:bg-red-600"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-20 text-center text-gray-500">
                No saved filters found
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer Actions */}
      {(() => {
        const areFiltersDifferent = (f1, f2) => {
          if (!f1 || !f2) return false;

          const keysToCompare = [
            "dateRange",
            "stageFilters",
            "leadScore",
            "interactionLevel",
            "assignedTo",
            "tags",
            "priority",
            "nationality",
            "stageId",
            "subStageId",
          ];

          for (const key of keysToCompare) {
            const val1 = f1[key];
            const val2 = f2[key];

            if (key === "dateRange") {
              const from1 = val1?.fromDate ? String(val1.fromDate) : "";
              const from2 = val2?.fromDate ? String(val2.fromDate) : "";
              const to1 = val1?.toDate ? String(val1.toDate) : "";
              const to2 = val2?.toDate ? String(val2.toDate) : "";
              if (from1 !== from2 || to1 !== to2) return true;
            } else if (key === "stageFilters") {
              const str1 = val1 ? JSON.stringify(val1) : "[]";
              const str2 = val2 ? JSON.stringify(val2) : "[]";
              if (str1 !== str2) return true;
            } else if (Array.isArray(val1) || Array.isArray(val2)) {
              const arr1 = Array.isArray(val1) ? val1 : val1 ? [val1] : [];
              const arr2 = Array.isArray(val2) ? val2 : val2 ? [val2] : [];
              if (
                arr1.length !== arr2.length ||
                arr1.some((v, i) => String(v) !== String(arr2[i]))
              ) {
                return true;
              }
            } else {
              const str1 =
                val1 !== null && val1 !== undefined ? String(val1) : "";
              const str2 =
                val2 !== null && val2 !== undefined ? String(val2) : "";
              if (str1 !== str2) return true;
            }
          }
          return false;
        };

        const hasActiveAdvanced = fieldGroups.some(
          (g) =>
            g.fieldId &&
            g.conditions.some(
              (c) =>
                c.operator &&
                (c.value || ["IS NULL", "IS NOT NULL"].includes(c.operator)),
            ),
        );
        const hasInvalidAdvanced = fieldGroups.some(
          (g) =>
            g.fieldId &&
            g.conditions.some(
              (c) =>
                !c.operator ||
                (!c.value && !["IS NULL", "IS NOT NULL"].includes(c.operator)),
            ),
        );
        const hasActiveQuick = Object.keys(currentFilters || {}).some(
          (k) =>
            ![
              "groupedFilters",
              "appliedSavedFilters",
              "leadview",
              "search",
              "applicantTab",
              "selectedColumns",
              "showLoader",
            ].includes(k) &&
            currentFilters[k] !== null &&
            currentFilters[k] !== undefined &&
            currentFilters[k] !== "" &&
            currentFilters[k] !== false &&
            (Array.isArray(currentFilters[k])
              ? currentFilters[k].length > 0
              : true),
        );

        // Track if quick or advanced filters changed relative to originally applied filters
        const quickFiltersChanged = areFiltersDifferent(
          currentFilters,
          appliedFilters,
        );

        const originalAdvancedGroups =
          appliedFilters?.groupedFilters?.Groups || [];
        const currentAdvancedGroups = fieldGroups.filter(
          (g) =>
            g.fieldId &&
            g.conditions.some(
              (c) =>
                c.operator &&
                (c.value || ["IS NULL", "IS NOT NULL"].includes(c.operator)),
            ),
        );
        const advancedFiltersChanged =
          originalAdvancedGroups.length !== currentAdvancedGroups.length ||
          JSON.stringify(originalAdvancedGroups.map((g) => g.Rules)) !==
          JSON.stringify(
            currentAdvancedGroups.map((g) => {
              const rules = g.conditions
                .filter(
                  (rule) =>
                    rule.operator &&
                    (rule.value ||
                      ["IS NULL", "IS NOT NULL"].includes(rule.operator)),
                )
                .map((rule) => ({
                  field: g.fieldId,
                  Operator: rule.operator,
                  Value: Array.isArray(rule.value)
                    ? rule.value.join(",")
                    : rule.value,
                }));
              return rules;
            }),
          );

        const filtersStateChanged =
          quickFiltersChanged || advancedFiltersChanged;

        const canApply =
          (hasActiveAdvanced || hasActiveQuick || filtersStateChanged) &&
          !hasInvalidAdvanced;
        const canSave =
          (hasActiveAdvanced || hasActiveQuick) && !hasInvalidAdvanced;

        const footerContent = (
          <div className="border-t border-gray-100 bg-white/95 p-4 pb-8 sm:pb-4 shadow-[0_-8px_20px_rgba(0,0,0,0.05)] backdrop-blur-md dark:border-gray-700 dark:bg-gray-800/95 dark:shadow-[0_-8px_20px_rgba(0,0,0,0.2)] pb-[calc(1rem+env(safe-area-inset-bottom,0px))]">
            <div className="flex items-center justify-between gap-4">
              {!editingFilterGroup ? (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="flex items-center gap-1.5 text-xs font-bold text-red-600 hover:text-red-700 dark:text-red-400"
                >
                  <TrashIcon className="h-4 w-4" /> Clear All
                </button>
              ) : (
                <div />
              )}
              <div className="flex gap-3">
                {editingFilterGroup ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowSaveModal(true)}
                      disabled={!canSave}
                      className="border-primary-200 text-primary-600 hover:bg-primary-50 dark:border-primary-800 dark:text-primary-400 flex items-center gap-1.5 rounded-xl border bg-white px-4 py-2 text-xs font-bold shadow-sm disabled:opacity-50 dark:bg-gray-700"
                    >
                      <BookmarkIcon className="h-4 w-4" /> Update
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="rounded-xl border border-gray-200 bg-white px-6 py-2 text-xs font-bold text-gray-700 shadow-sm transition-all hover:bg-gray-50 active:scale-95 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowSaveModal(true)}
                      disabled={!canSave}
                      className="border-primary-200 text-primary-600 hover:bg-primary-50 dark:border-primary-800 dark:text-primary-400 flex items-center gap-1.5 rounded-xl border bg-white px-4 py-2 text-xs font-bold shadow-sm disabled:opacity-50 dark:bg-gray-700"
                    >
                      <BookmarkIcon className="h-4 w-4" /> Save
                    </button>
                    <button
                      type="button"
                      onClick={handleApplyFilters}
                      disabled={isApplying || !canApply}
                      className="bg-primary-600 shadow-primary-500/20 hover:bg-primary-700 rounded-xl px-6 py-2 text-xs font-bold text-white shadow-lg transition-all active:scale-95 disabled:opacity-50"
                    >
                      {isApplying ? "Applying..." : "Apply Filters"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );

        return footerPortalTarget ? (
          createPortal(footerContent, footerPortalTarget)
        ) : (
          <div className="sticky bottom-0 z-[60] -mx-3 mt-6 -mb-3">
            {footerContent}
          </div>
        );
      })()}

      {/* Save Modal */}
      {typeof document !== "undefined" &&
        createPortal(
          <Transition show={showSaveModal} as={Fragment}>
            <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
              <TransitionChild
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"
                onClick={() => !isSaving && setShowSaveModal(false)}
              />
              <TransitionChild
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
                className="relative z-10 w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl dark:bg-gray-800"
              >
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {editingFilterGroup
                    ? "Update Filter Template"
                    : "Save as Template"}
                </h3>
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="mb-1.5 block text-xs font-bold text-gray-500">
                      Template Name
                    </label>
                    <input
                      type="text"
                      value={saveFilterName}
                      onChange={(e) => setSaveFilterName(e.target.value)}
                      placeholder="e.g. High Value Leads"
                      className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-bold text-gray-500">
                      Description (Optional)
                    </label>
                    <textarea
                      value={saveFilterNote}
                      onChange={(e) => setSaveFilterNote(e.target.value)}
                      placeholder="What is this filter for?"
                      rows={3}
                      className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <Button
                    variant="flat"
                    onClick={() => setShowSaveModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSaveFilters} disabled={isSaving}>
                    {isSaving ? "Saving..." : "Save Template"}
                  </Button>
                </div>
              </TransitionChild>
            </div>
          </Transition>,
          document.body,
        )}

      {/* Group Editor Modal */}
      {typeof document !== "undefined" &&
        drawerPortalTarget &&
        createPortal(
          <Transition show={!!activeModalGroupId} as={Fragment}>
            <div className="absolute inset-0 z-[99999] flex flex-col items-center justify-center p-4">
              <TransitionChild
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
                onClick={handleCancelGroupEdit}
              />
              <TransitionChild
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
                className="relative z-10 flex max-h-full w-[95%] flex-col rounded-2xl bg-white p-5 shadow-2xl sm:max-w-xl dark:bg-gray-800"
              >
                {(() => {
                  // Find the group, but fallback to a valid group during exit animation
                  const foundIdx = fieldGroups.findIndex(
                    (g) => g.id === activeModalGroupId,
                  );
                  const groupIdx = foundIdx >= 0 ? foundIdx : 0;
                  const group = fieldGroups[groupIdx];

                  if (!group) return null;

                  return (
                    <div className="flex min-h-0 flex-col">
                      <div className="mb-6 flex shrink-0 items-center justify-between">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {group.fieldId
                            ? "Edit Filter Group"
                            : "Add Filter Group"}
                        </h3>
                        <button
                          onClick={handleCancelGroupEdit}
                          className="text-gray-400 hover:text-gray-500"
                        >
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>

                      <div
                        className="min-h-0 flex-1 overflow-y-auto pr-2"
                        onScroll={() => setOpenDropdown(null)}
                      >
                        <div className="space-y-1 pb-2">
                          {group.conditions.map((cond, condIdx) => (
                            <div
                              key={cond.id}
                              className={clsx(
                                "relative flex items-center gap-2 transition-all",
                                condIdx > 0 ? "mt-2" : "",
                                openDropdown?.includes(
                                  `-${groupIdx}-${condIdx}`,
                                )
                                  ? "z-40"
                                  : "z-0",
                              )}
                            >
                              <div className="min-w-[150px] flex-1">
                                {condIdx === 0 ? (
                                  <>
                                    <label className="mb-1.5 block text-[11px] font-bold text-gray-500">
                                      Select Field
                                    </label>
                                    {renderCustomDropdown(
                                      `field-${groupIdx}`,
                                      getGroupedFieldOptions(group.id),
                                      group.fieldId,
                                      (v) => updateFieldGroup(groupIdx, v),
                                      "Field...",
                                      true,
                                      true,
                                      true,
                                    )}
                                  </>
                                ) : (
                                  <div className="flex h-[38px] items-center justify-end pr-2">
                                    <span className="text-primary-500 bg-primary-50 dark:bg-primary-900/20 border-primary-100 dark:border-primary-900/30 rounded border px-3 py-1 text-[11px] font-bold tracking-wider uppercase shadow-sm">
                                      OR
                                    </span>
                                  </div>
                                )}
                              </div>

                              <div className="w-[130px] shrink-0">
                                {condIdx === 0 && (
                                  <label className="mb-1.5 block text-[11px] font-bold text-gray-500">
                                    Operator
                                  </label>
                                )}
                                {renderCustomDropdown(
                                  `operator-${groupIdx}-${condIdx}`,
                                  getOperatorsForField(),
                                  cond.operator,
                                  (v) =>
                                    updateCondition(
                                      groupIdx,
                                      condIdx,
                                      "operator",
                                      v,
                                    ),
                                  "Operator...",
                                  true,
                                )}
                              </div>

                              {!["IS NULL", "IS NOT NULL"].includes(
                                cond.operator,
                              ) && (
                                  <div className="w-[180px] shrink-0">
                                    {condIdx === 0 && (
                                      <label className="mb-1.5 block text-[11px] font-bold text-gray-500">
                                        Value
                                      </label>
                                    )}
                                    {(() => {
                                      const field = masterFields.find(
                                        (f) => String(f.id).toLowerCase() === String(group.fieldId).toLowerCase()
                                      );
                                      const options = getFieldOptions(
                                        group.fieldId,
                                      );
                                      if (field?.htmlInputType === "date") {
                                        return (
                                          <DatePicker
                                            value={cond.value || ""}
                                            onChange={(dates, dateStr) =>
                                              updateCondition(
                                                groupIdx,
                                                condIdx,
                                                "value",
                                                dateStr,
                                              )
                                            }
                                            options={{
                                              altInput: true,
                                              altFormat: "d/m/y",
                                              dateFormat: "Y-m-d",
                                            }}
                                            className="w-full"
                                          />
                                        );
                                      } else if (options.length > 0) {
                                        // Single-select dropdown — only one value allowed
                                        const currentVal = Array.isArray(
                                          cond.value,
                                        )
                                          ? (cond.value[0] ?? "")
                                          : cond.value || "";
                                        return renderCustomDropdown(
                                          `value-${groupIdx}-${condIdx}`,
                                          options,
                                          currentVal,
                                          (v) =>
                                            updateCondition(
                                              groupIdx,
                                              condIdx,
                                              "value",
                                              v,
                                            ),
                                          "Value...",
                                          true, // compact
                                          true, // enableSearch
                                        );
                                      }
                                      return (
                                        <input
                                          type="text"
                                          value={cond.value}
                                          onChange={(e) =>
                                            updateCondition(
                                              groupIdx,
                                              condIdx,
                                              "value",
                                              e.target.value,
                                            )
                                          }
                                          placeholder="Value..."
                                          className="focus:border-primary-500 focus:ring-primary-500/20 w-full rounded-lg border border-gray-300 bg-white px-2 py-1 text-[13px] focus:ring-1 focus:outline-none dark:border-gray-600 dark:bg-gray-700"
                                        />
                                      );
                                    })()}
                                  </div>
                                )}

                              {group.conditions.length > 1 && (
                                <button
                                  onClick={() => {
                                    const updated = [...fieldGroups];
                                    const group = updated[groupIdx];
                                    updated[groupIdx] = {
                                      ...group,
                                      conditions: group.conditions.filter(
                                        (_, i) => i !== condIdx,
                                      ),
                                    };
                                    setFieldGroups(updated);
                                  }}
                                  className={clsx(
                                    "text-gray-400 transition-colors hover:text-red-500",
                                    condIdx === 0
                                      ? "top-3.5 translate-y-3"
                                      : "top-1/2",
                                    ["IS NULL", "IS NOT NULL"].includes(
                                      cond.operator,
                                    ) && "right-0",
                                  )}
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>

                        <div className="flex items-center gap-4 pt-5 pb-2">
                          <button
                            onClick={() => addConditionToGroup(groupIdx)}
                            className="text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1 text-[11px] font-bold"
                          >
                            <PlusIcon className="h-3 w-3" /> Add Condition
                          </button>
                        </div>
                      </div>

                      <div className="mt-4 flex shrink-0 justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
                        <Button variant="flat" onClick={handleCancelGroupEdit}>
                          Cancel
                        </Button>
                        <Button
                          onClick={handleDoneGroupEdit}
                          disabled={
                            !group.fieldId ||
                            group.conditions.some(
                              (c) =>
                                !c.operator ||
                                (!c.value &&
                                  !["IS NULL", "IS NOT NULL"].includes(
                                    c.operator,
                                  )),
                            )
                          }
                        >
                          Done
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </TransitionChild>
            </div>
          </Transition>,
          drawerPortalTarget,
        )}

      <ConfirmModal
        show={showDeleteConfirm}
        state="pending"
        confirmLoading={isDeleting}
        onClose={() => setShowDeleteConfirm(false)}
        onOk={onConfirmDelete}
        buttonColor="error"
        messages={{
          pending: {
            title: "Delete Filter Template",
            description: `Permanently delete "${filterToDelete?.name}"?`,
            actionText: "Delete",
          },
        }}
      />
    </div>
  );
}
